import { css } from "@emotion/react";
import { ElementType, type KeyboardEvent } from "react";
import { DoNotCare } from "../types";
import { ClickableProps } from "./types";

/**
 * `Clickable` is a component that can be used to make any element (or group of elements) clickable.
 */
function Clickable<T extends ElementType = "button">({
  children,
  as,
  disabled,
  onClick,
  onKeyDown,
  onFocus,
  onBlur,
  "aria-label": ariaLabel,
  "aria-expanded": ariaExpanded,
  role: _role,
  className,
  canSelect
}: ClickableProps<T>) {
  const Component = as || "button";
  const isButton = Component === "button";
  let role = _role;
  if (!role && !isButton) {
    role = "button";
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (!isButton && !disabled && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onClick?.(e as DoNotCare);
    }
    onKeyDown?.(e);
  };

  return (
    <Component
      className={`kit-Clickable ${className ? className : ""}`}
      type={isButton ? "button" : undefined}
      role={role}
      tabIndex={!disabled ? 0 : -1}
      onClick={disabled ? undefined : onClick}
      onKeyDown={handleKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
      disabled={isButton ? disabled : undefined}
      aria-disabled={!isButton && disabled ? true : undefined}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      css={[
        css`
          all: unset;
          cursor: ${disabled ? "default" : "pointer"};
        `,
        !canSelect &&
          css`
            user-select: none;
          `
      ]}
    >
      {children}
    </Component>
  );
}

export default Clickable;
export * from "./types";
